import i18next from "i18next";
import { Dispatch } from "redux";
import { analyticsFEA } from "src/AnalyticsFEA";
import { GarageKeyType } from "src/components/UserAccount/shared/GarageKey";
import { MAX_VEHICLES } from "src/constants";
import { getAppState } from "src/store";
import {
    authenticationMergeRequest,
    authenticationMergeSuccess,
    getDealPersonSuccess,
    getDealSuccess,
    postDealRequest,
    updateDealVehicles,
} from "src/store/actions/deal";
import { personAuthFailure, personAuthRequest, personAuthSuccess } from "src/store/actions/personAuth";
import { patchTrackingFailure, patchTrackingRequest, patchTrackingSuccess } from "src/store/actions/tracking";
import { updateAuth } from "src/store/actions/userAccount";
import { postVehicleFailure, postVehicleRequest, postVehicleSuccess } from "src/store/actions/vehicles";
import { EXPAND_ALL } from "src/store/constants/deal";
import { ActionType } from "src/store/dataService/actions/utils/actionCreator";
import { forgetLocalDealState, setLocalDealState } from "src/utils/localStorage";
import { Deal } from "Types/api/Deal";
import { Tracking } from "Types/api/Tracking";
import { ApiVehicle } from "Types/api/Vehicle";
import { AccountState, OnlineShopperState } from "Types/state";
import {
    createDealErrorAction,
    createDealSuccessAction,
    DealAction,
    GetDealObject,
    GetDealPersonObject,
    PatchDealPersonObject,
} from "../actions/deal";
import { DealResponses, DealRoutes } from "../types";
import { getFromAuthed, patchTo, postTo } from "./shared/osfetch";
import { findRoute } from "./util";

const cleanDealPersonResponse = (json: any) => {
    return {
        person: json.person,
    };
};

export const postDeal = (data: any, next: Dispatch) => {
    return postTo("/deals", data, next);
};

export const createDeal = async (
    next: Dispatch,
    { payload: { env, accountSlug, vin, paymentType, tracking } }: ActionType<typeof postDealRequest>,
) => {
    const response = await postDeal(
        {
            ...(Number.isNaN(Number.parseInt(accountSlug)) ? { account_slug: accountSlug } : { ccid: Number.parseInt(accountSlug) }),
            vins: vin ? [vin] : [],
            initial_payment_type: paymentType,
            tracking,
        },
        next
    );

    if (response.ok) {
        const data = await response.json();

        next(
            createDealSuccessAction({
                current_data_revision: data.current_data_revision,
                uuid: data.uuid,
                experiments: data.experiments,
            }),
        );

        analyticsFEA.updateOSProvider(data.uuid);

        setLocalDealState({
            env,
            slug: accountSlug,
            uuid: data.uuid,
            garageCreated: false,
            isAuthed: false,
        });

        const vehicles = data.vehicles;

        if (vehicles) {
            next(updateDealVehicles(vehicles));
            analyticsFEA.engine.dealCreated(vin);
        }

        if (data.person) {
            next(getDealPersonSuccess(data.person));
        }

        return;
    }

    if (response.status === 404) {
        const data = await response.json();
        next(createDealErrorAction(data.message));
        return;
    }

    next(createDealErrorAction(i18next.t("Modal.unexpectedIssueMessage")));
};

export const getDeal = async (next: Dispatch, action: DealAction) => {
    const actionData = action.data as GetDealObject;
    const response = await getFromAuthed(`/deals/${actionData.uuid}?${EXPAND_ALL}`);

    if (response.ok) {
        const data = await response.json();

        next(getDealSuccess(data));
    } else {
        if ([401, 404].includes(response.status)) {
            next({
                type: DealResponses.GetDealError,
            });
            next(updateAuth(false));

            const { onlineShopper = {} as OnlineShopperState, account = {} as AccountState } = getAppState();
            if (onlineShopper.env && account.slug) {
                forgetLocalDealState({ slug: account.slug, env: onlineShopper.env });
            }
        }
    }
};

export const getDealPerson = async (next: Dispatch, action: DealAction) => {
    const actionData = action.data as GetDealPersonObject;
    const response = await getFromAuthed(`/deals/${actionData.uuid}?expand[person]=*`);

    if (response.ok) {
        const responseJson = await response.json();
        const data = cleanDealPersonResponse(responseJson);

        next(getDealPersonSuccess(data.person));
    } else {
        next({
            data: { person: null },
            type: DealResponses.GetDealPersonError,
        });
    }
};

/**
 * Checks if this deal person is truthy, and if all its values are truthy.
 * @param person C
 */
const isInvalidPerson = (person: Deal["person"]) => !person || Object.keys(person).forEach((key) => !!person[key]);

const dispatchPersonError = {
    data: { person: null },
    type: DealResponses.PatchDealPersonError,
};

const dispatchRevisionIncrement = {
    type: DealResponses.IncrementRevision,
};

export const patchDealPerson = async (next: Dispatch, action: DealAction) => {
    const actionData = action.data as PatchDealPersonObject;

    if (!actionData.dealPerson || isInvalidPerson(actionData.dealPerson)) {
        next(dispatchPersonError);
        return;
    }
    const response = await patchTo(`/deals/${actionData.uuid}/person`, actionData.dealPerson);
    if (!response.ok) {
        next(dispatchPersonError);
        return;
    }

    const responseJson = await response.json();

    next({
        data: { person: responseJson },
        type: DealResponses.PatchDealPersonSuccess,
    });

    const isPreferencePatch = Object.keys(actionData.dealPerson).length === 1 && !!actionData.dealPerson.preferences;

    if (!isPreferencePatch) {
        next(dispatchRevisionIncrement);
    }
};

export const addVehicle = async (next: Dispatch, action: ActionType<typeof postVehicleRequest>) => {
    if (
        action.payload.vin in action.payload.currentVehicles &&
        !action.payload.currentVehicles[action.payload.vin]?.isDeleting
    ) {
        next(postVehicleFailure(action.payload.vin, "vehicle_exists"));
        return;
    }

    if (Object.keys(action.payload.currentVehicles).length >= MAX_VEHICLES) {
        next(postVehicleFailure(action.payload.vin, "vehicle_limit"));
        return;
    }

    const response = await postTo(
        `/deals/${action.payload.uuid}/vehicles?expand[account.integration_settings]=*`,
        { vin: action.payload.vin, initial_payment_type: action.payload.paymentType, sync_selected_payment: false },
        next,
        false,
    );

    const result = await response.json();

    if (response.status === 400 && result?.exception === "App\\\\Exceptions\\\\Models\\\\UniqueException") {
        // @see ONSHOP-7545
        next(postVehicleFailure(action.payload.vin, "vehicle_exists"));
        return;
    }

    if (!response.ok) {
        next(postVehicleFailure(action.payload.vin, "unexpected_issue"));
        return;
    }

    const vehicle: ApiVehicle = result;
    analyticsFEA.engine.vehicleAdded(vehicle.vin);
    next(postVehicleSuccess(vehicle));

    // do the afterFetch if it exists (introduced by https://carsenterprise.atlassian.net/browse/MR-459)
    if (action.payload.afterFetch) action.payload.afterFetch();
};

export const patchTracking = async (
    next: Dispatch,
    { payload: { uuid, tracking } }: ActionType<typeof patchTrackingRequest>,
) => {
    if (tracking.ccdp !== undefined && (getAppState().tracking?.ccdp?.visitor_ids ?? []).length) {
        tracking.ccdp.visitor_ids = getAppState().tracking.ccdp?.visitor_ids!;
    }

    const response = await patchTo<Tracking>(`/deals/${uuid}/tracking`, tracking, next, false);

    if (response.ok) {
        next(patchTrackingSuccess());
    } else {
        next(patchTrackingFailure());
    }
};

const onSuccessfulAuthentication = async (
    next: Dispatch,
    {
        payload: {
            dealData: { uuid, verifiedUuid, garageKeyType, vins },
            envConfig: { environment, slug },
            garageCreated,
        },
    }: ActionType<typeof authenticationMergeRequest>,
) => {
    const dealResponse = await getFromAuthed(`/deals/${verifiedUuid}?${EXPAND_ALL}`);

    const deal: Deal = await dealResponse.json();

    const [personResponse, _trackingResponse, ...vehicleResponses] = await Promise.all([
        garageKeyType
            ? patchTo(`/deals/${verifiedUuid}/person`, {
                  preferences: {
                      method_of_contact: garageKeyType,
                  },
              })
            : Promise.resolve({ json: () => Promise.resolve(deal.person) }),
        patchTo(`/deals/${verifiedUuid}/tracking`, {
            ccdp: {
                visitor_ids: [
                    ...new Set([
                        ...(getAppState().tracking.ccdp?.visitor_ids ?? []),
                        ...(deal?.tracking?.ccdp?.visitor_ids ?? []),
                    ]),
                ],
            },
        }),
        ...(uuid !== verifiedUuid
            ? vins
                  .filter((vin) => (deal.vehicles?.findIndex((vehicle) => vehicle.vin === vin) ?? -1) < 0)
                  .slice(0, 6 - (deal.vehicles?.length ?? 0))
                  .map((vin) =>
                      postTo(`/deals/${verifiedUuid}/vehicles?expand[account.integration_settings]=*`, { vin }),
                  )
            : []),
    ]);

    next(
        authenticationMergeSuccess({
            ...deal,
            current_data_revision:
                uuid !== verifiedUuid ? (deal?.current_data_revision ?? 0) + 0.1 : deal.current_data_revision,
            person: await personResponse.json(),
            vehicles: [
                ...(deal.vehicles ?? []),
                ...(await Promise.all(vehicleResponses.map(async (response) => response.json()))),
            ],
        }),
    );

    setLocalDealState({ slug, env: environment, uuid: verifiedUuid, garageCreated, isAuthed: true });
    analyticsFEA.engine.unlockPricing();
};

/**
 * Calls the person/auth API, which both patches person *and* tells the server to send the customer a PIN
 * @param next
 * @param uuid
 * @param person
 * @param sendPinTo
 */
export const postPersonAuth = async (
    next: Dispatch,
    { payload: { uuid, person, sendPinTo } }: ActionType<typeof personAuthRequest>,
) => {
    const response = await postTo(
        `/deals/${uuid}/person/auth`,
        { person, send_pin_to: sendPinTo === GarageKeyType.phone ? "sms" : "email" },
        next,
    );

    const data = await response.json();
    if (response.ok) {
        next(personAuthSuccess(data));
    } else {
        next(personAuthFailure(data.message));
    }
};

const routes = {
    [postVehicleRequest.type]: addVehicle,
    [postDealRequest.type]: createDeal,
    [DealRoutes.GetDeal]: getDeal,
    [DealRoutes.GetDealPerson]: getDealPerson,
    [DealRoutes.PatchDealPerson]: patchDealPerson,
    [patchTrackingRequest.type]: patchTracking,
    [authenticationMergeRequest.type]: onSuccessfulAuthentication,
    [personAuthRequest.type]: postPersonAuth,
};

export default (next: Dispatch, action: any) => findRoute(routes)(next, action);
